import type { ArtifactCategory, ArtifactType } from '@/config/objects/artifacts';
import {
  HandCoins,
  MoonStar,
  Shield,
  Shuffle,
  SignpostBig,
  Snowflake,
  Sparkles,
  Sword,
  Zap,
  type LucideIcon,
} from 'lucide-react';

/**
 * Whose phone the demo shows while the effect runs. Offensive effects land on
 * a rival team's device; defensive and self-serving effects play out on the
 * using team's own device.
 */
export type EffectPov = 'rival' | 'own';

export interface ArtifactEffectMeta {
  icon: LucideIcon;
  /** One-line answer to "what does this do?" shown next to the demo. */
  summary: string;
  /** Whose screen the vignette plays on. */
  pov: EffectPov;
  /** Step-by-step of how the effect plays out in a hunt. */
  steps: readonly string[];
}

/**
 * Gameplay meaning of each artifact type. The server records effects
 * generically (only `shield` has hard mechanics — it is consumed to block an
 * incoming offensive artifact); these describe how each type is presented to
 * participants and is intended to be played.
 */
export const ARTIFACT_EFFECT_META: Record<ArtifactType, ArtifactEffectMeta> = {
  scramble: {
    icon: Shuffle,
    summary:
      'Garbles the target team’s screen — text turns jumbled and hard to read until the effect expires.',
    pov: 'rival',
    steps: [
      'Your team uses the artifact and picks a target team',
      'The target’s device shows an "Under attack" alert',
      'Their challenge text scrambles for the duration',
      'A countdown banner shows when it wears off',
    ],
  },
  freeze: {
    icon: Snowflake,
    summary:
      'Locks the target team out — their screen frosts over and they cannot interact until it thaws.',
    pov: 'rival',
    steps: [
      'Your team uses the artifact and picks a target team',
      'The target’s screen frosts over immediately',
      'They cannot open or submit challenges while frozen',
      'The freeze thaws when the countdown ends',
    ],
  },
  blackout: {
    icon: MoonStar,
    summary:
      'Blacks out the target team’s map so they navigate blind until the effect expires.',
    pov: 'rival',
    steps: [
      'Your team uses the artifact and picks a target team',
      'The target’s hunt map goes dark',
      'Pins and routes are hidden for the duration',
      'The map lights back up when the countdown ends',
    ],
  },
  steal: {
    icon: HandCoins,
    summary:
      'Sets a trap on the target team: the next time they complete a challenge for points, those points come to you instead.',
    pov: 'rival',
    steps: [
      'Your team uses the artifact and picks a target team',
      'The steal waits quietly — the target never sees it coming',
      'The next challenge they complete for points is stolen',
      'Those points transfer to your team; facilitators see the swing',
    ],
  },
  misdirect: {
    icon: SignpostBig,
    summary:
      'Sends the target team’s map pins astray so they chase the wrong location.',
    pov: 'rival',
    steps: [
      'Your team uses the artifact and picks a target team',
      'Pins on the target’s map drift off their true spots',
      'The team wastes time walking the wrong way',
      'Pins snap back when the effect expires',
    ],
  },
  double_down: {
    icon: Zap,
    summary:
      'Doubles the points your team earns while the effect is active.',
    pov: 'own',
    steps: [
      'Claim one on the map and it activates instantly; arsenal copies you activate yourself',
      'A ×2 marker appears on your point values',
      'Challenges completed during the window score double',
      'The multiplier ends with the countdown',
    ],
  },
  shield: {
    icon: Shield,
    summary:
      'Protects your team: the next offensive artifact aimed at you is blocked and the shield is consumed.',
    pov: 'own',
    steps: [
      'Your team activates the shield ahead of time',
      'A rival targets you with an offensive artifact',
      'The attack bounces off — the server consumes the shield instead',
      'Your team never feels the effect',
    ],
  },
};

/**
 * Category presentation used by the demo's applied-modal and banner —
 * mirrors the mobile app's EffectAppliedModal / EffectBanner theming.
 */
export const EFFECT_CATEGORY_THEME: Record<
  ArtifactCategory,
  { icon: LucideIcon; label: string; appliedTitle: string; rivalTitle: string; accent: string; soft: string }
> = {
  offensive: {
    icon: Sword,
    label: 'Offensive',
    appliedTitle: 'Attack launched!',
    rivalTitle: 'Under attack!',
    accent: 'bg-red-500',
    soft: 'bg-red-500/15 text-red-400',
  },
  defensive: {
    icon: Shield,
    label: 'Defensive',
    appliedTitle: 'Shield activated',
    rivalTitle: 'Shield activated',
    accent: 'bg-blue-500',
    soft: 'bg-blue-500/15 text-blue-400',
  },
  wildcard: {
    icon: Sparkles,
    label: 'Wildcard',
    appliedTitle: 'Effect applied',
    rivalTitle: 'Effect applied',
    accent: 'bg-purple-500',
    soft: 'bg-purple-500/15 text-purple-400',
  },
};
